import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { tasksAPI, leadsAPI, usersAPI } from '../services/api';
import { useToast } from './Toast';
import { formatPhoneDisplay } from '../utils/formatters';
import { 
  ChevronDown, ChevronRight, Calendar, User, 
  CheckCircle, Pencil, Trash2, Search, Plus, Clock, X, Phone, AlertCircle, CalendarDays, CheckCircle2, Circle, MoreVertical
} from 'lucide-react';

// Custom Searchable Dropdown
const SearchableSelect = ({ options, value, onChange, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(o => o.value == value);
  const filteredOptions = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative" ref={wrapperRef}>
      <div 
        className={`w-full border ${isOpen ? 'border-[#1E3A5F] ring-1 ring-[#1E3A5F]/20' : 'border-slate-200'} bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] cursor-pointer flex items-center justify-between ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={selectedOption ? 'text-slate-800 font-medium' : 'text-slate-400'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg flex flex-col">
          <div className="p-2 border-b border-slate-100 shrink-0">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-[13px] outline-none focus:border-[#1E3A5F]"
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48 p-1">
            <div 
              className="px-3 py-2 text-[13px] text-slate-500 hover:bg-slate-50 rounded-md cursor-pointer mb-1"
              onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}
            >
              Clear selection
            </div>
            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => (
                <div 
                  key={opt.value}
                  className={`px-3 py-2 text-[13px] rounded-md cursor-pointer ${value == opt.value ? 'bg-[#1E3A5F]/10 text-[#1E3A5F] font-semibold' : 'text-slate-700 hover:bg-slate-50'}`}
                  onClick={() => { onChange(opt.value); setIsOpen(false); setSearch(''); }}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="px-3 py-4 text-[13px] text-slate-400 text-center">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Tasks = () => {
  const { showToast } = useToast();
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTask, setEditingTask] = useState(null);
  const [collapsed, setCollapsed] = useState({
    overdue: false,
    today: false,
    upcoming: false,
    completed: false
  });
  const [activeMenu, setActiveMenu] = useState(null); // for row actions

  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    loadTasks();
    const handleClickOutside = () => setActiveMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await tasksAPI.getAll();
      const raw = res.data;
      const taskArray = Array.isArray(raw) ? raw : (raw?.tasks || raw?.data || []);
      setTasks(taskArray);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      setLoadError(error.response?.data?.error || 'Failed to load tasks. Check your connection.');
      showToast('Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (task) => {
    if (task.status === 'completed') return;
    try {
      await tasksAPI.update(task.id, { status: 'completed' });
      showToast('Task marked as completed ✓', 'success');
      loadTasks();
    } catch (error) {
      console.error('Failed to complete task:', error);
      showToast(error.response?.data?.error || 'Failed to complete task', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await tasksAPI.delete(id);
      showToast('Task deleted successfully', 'success');
      loadTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
      showToast('Failed to delete task', 'error');
    }
  };

  const toggleCollapse = (section) => {
    setCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);

  const groups = { overdue: [], today: [], upcoming: [], completed: [] };

  const searchLower = searchQuery.toLowerCase();
  (Array.isArray(tasks) ? tasks : []).forEach(task => {
    if (!task || typeof task !== 'object') return;

    if (searchQuery) {
      const matchesTitle = (task.title || '').toLowerCase().includes(searchLower);
      const matchesLead = task.lead_name && task.lead_name.toLowerCase().includes(searchLower);
      const matchesPhone = task.lead_phone && task.lead_phone.includes(searchQuery);
      if (!matchesTitle && !matchesLead && !matchesPhone) return;
    }

    const status = task.status || 'pending';
    if (status === 'completed') { groups.completed.push(task); return; }
    if (status === 'cancelled') return;

    if (!task.due_date) { groups.upcoming.push(task); return; }

    const dueDate = new Date(task.due_date);
    if (isNaN(dueDate.getTime())) { groups.upcoming.push(task); return; }

    const dateOnly = new Date(dueDate);
    dateOnly.setHours(0, 0, 0, 0);

    if (dateOnly < todayDate) groups.overdue.push(task);
    else if (dateOnly.getTime() === todayDate.getTime()) groups.today.push(task);
    else groups.upcoming.push(task);
  });

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return <span className="flex items-center gap-1.5 text-rose-600 font-semibold text-[13px]"><span className="w-1.5 h-1.5 rounded-full bg-rose-600"></span>High</span>;
      case 'medium': return <span className="flex items-center gap-1.5 text-amber-600 font-semibold text-[13px]"><span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>Medium</span>;
      case 'low': return <span className="flex items-center gap-1.5 text-slate-500 font-semibold text-[13px]"><span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>Low</span>;
      default: return <span className="text-slate-500">Normal</span>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return <span className="bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-md text-[12px] font-bold border border-emerald-100">Completed</span>;
      case 'in_progress': return <span className="bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-md text-[12px] font-bold border border-indigo-100">In Progress</span>;
      case 'pending': return <span className="bg-amber-50 text-amber-700 px-2.5 py-1 rounded-md text-[12px] font-bold border border-amber-100">Pending</span>;
      case 'cancelled': return <span className="bg-slate-50 text-slate-700 px-2.5 py-1 rounded-md text-[12px] font-bold border border-slate-200">Cancelled</span>;
      default: return <span className="bg-slate-50 text-slate-700 px-2.5 py-1 rounded-md text-[12px] font-bold border border-slate-200">{(status||'').replace('_', ' ')}</span>;
    }
  };

  const formatDue = (isoString) => {
    if (!isoString) return '-';
    const d = new Date(isoString);
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };

  const renderTableSection = (key, title, tasksArray) => {
    const isCollapsed = collapsed[key];
    
    return (
      <div className="mb-6 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div 
          className="bg-slate-50/80 px-5 py-3 flex items-center justify-between cursor-pointer border-b border-slate-200"
          onClick={() => toggleCollapse(key)}
        >
          <div className="flex items-center gap-2">
            <button className="text-slate-400 hover:text-slate-700 transition-colors">
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
            </button>
            <span className="font-bold text-slate-800 text-[15px] tracking-tight">{title} <span className="text-slate-400 ml-1 text-[13px] font-semibold">({tasksArray.length})</span></span>
          </div>
        </div>

        {!isCollapsed && (
          <div className="overflow-visible pb-16">
            <table className="w-full text-left border-collapse min-w-[800px]">
              <thead>
                <tr className="bg-white border-b border-slate-200">
                  <th className="py-3 px-5 text-[12px] font-bold text-slate-500 uppercase tracking-wider w-10 text-center"><input type="checkbox" className="rounded border-slate-300" disabled /></th>
                  <th className="py-3 px-5 text-[12px] font-bold text-slate-500 uppercase tracking-wider">Description</th>
                  <th className="py-3 px-5 text-[12px] font-bold text-slate-500 uppercase tracking-wider">Members</th>
                  <th className="py-3 px-5 text-[12px] font-bold text-slate-500 uppercase tracking-wider">Start Date</th>
                  <th className="py-3 px-5 text-[12px] font-bold text-slate-500 uppercase tracking-wider">Priority</th>
                  <th className="py-3 px-5 text-[12px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="py-3 px-5 text-[12px] font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tasksArray.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-8 text-center text-slate-400 text-sm">No tasks in this section.</td>
                  </tr>
                ) : (
                  tasksArray.map(task => (
                    <tr key={task.id} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="py-4 px-5 text-center align-middle">
                        <input type="checkbox" className="rounded border-slate-300 text-[#1E3A5F] focus:ring-[#1E3A5F] cursor-pointer" 
                          checked={task.status === 'completed'}
                          onChange={() => handleComplete(task)}
                        />
                      </td>
                      <td className="py-4 px-5 align-middle">
                        <div className="font-bold text-slate-800 text-[14px] leading-tight mb-1">{task.title}</div>
                        {task.lead_id && (
                          <Link to={`/leads/${task.lead_id}`} className="text-[#1E3A5F] hover:underline text-[12px] font-semibold flex items-center gap-1">
                            {task.lead_name || `Lead #${task.lead_id}`}
                            {task.lead_phone && <span className="text-slate-400 font-normal ml-1">— {task.lead_phone}</span>}
                          </Link>
                        )}
                      </td>
                      <td className="py-4 px-5 align-middle">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center text-[10px] font-bold" title={task.assigned_to_name}>
                            {(task.assigned_to_name || 'U')[0].toUpperCase()}
                          </div>
                          <span className="text-[13px] text-slate-600 font-medium">{task.assigned_to_id === currentUser?.id ? 'You' : task.assigned_to_name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5 text-[13px] text-slate-600 font-medium align-middle">
                        {formatDue(task.due_date)}
                      </td>
                      <td className="py-4 px-5 align-middle">
                        {getPriorityBadge(task.priority)}
                      </td>
                      <td className="py-4 px-5 align-middle">
                        {getStatusBadge(task.status)}
                      </td>
                      <td className="py-4 px-5 text-right relative align-middle">
                        <button 
                          onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === task.id ? null : task.id); }}
                          className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                        >
                          <MoreVertical size={16} />
                        </button>
                        {activeMenu === task.id && (
                          <div className="absolute right-6 top-10 w-36 bg-white border border-slate-200 shadow-xl rounded-xl py-1.5 z-50 animate-in fade-in duration-150">
                            {task.status !== 'completed' && (
                              <button onClick={(e) => { e.stopPropagation(); handleComplete(task); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                <CheckCircle size={14} className="text-emerald-500" /> Complete
                              </button>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); setEditingTask(task); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                              <Pencil size={14} className="text-blue-500" /> Edit
                            </button>
                            {isAdmin && (
                              <button onClick={(e) => { e.stopPropagation(); handleDelete(task.id); setActiveMenu(null); }} className="w-full text-left px-4 py-2 text-[13px] font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-2">
                                <Trash2 size={14} /> Delete
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F4F2]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Tasks</h1>
          
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="relative flex-1 sm:flex-initial min-w-0">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-[200px] lg:w-[280px] pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] outline-none text-[13px] shadow-sm transition-all"
              />
            </div>
            <button className="px-3 sm:px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-[13px] flex items-center gap-2 hover:bg-slate-50 shadow-sm transition-colors">
              <span className="text-slate-400 hidden sm:inline">Y</span> Filters
            </button>
            <button
              onClick={() => setEditingTask({})}
              className="bg-[#1E3A5F] hover:bg-[#132845] text-white px-4 sm:px-5 py-2.5 rounded-xl flex items-center gap-2 text-[13px] font-bold shadow-sm transition-all"
            >
              <Plus size={16} strokeWidth={2.5} />
              <span className="hidden sm:inline">New Task</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col">
            <div className="flex items-center gap-2 text-slate-500 font-semibold text-[13px] mb-2">
              <AlertCircle size={16} className="text-rose-500" /> Not Started / Overdue
            </div>
            <div className="text-3xl font-extrabold text-slate-800">{groups.overdue.length}</div>
          </div>
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col">
            <div className="flex items-center gap-2 text-slate-500 font-semibold text-[13px] mb-2">
              <Calendar size={16} className="text-indigo-500" /> In Progress / Today
            </div>
            <div className="text-3xl font-extrabold text-slate-800">{groups.today.length}</div>
          </div>
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col">
            <div className="flex items-center gap-2 text-slate-500 font-semibold text-[13px] mb-2">
              <CalendarDays size={16} className="text-amber-500" /> Upcoming
            </div>
            <div className="text-3xl font-extrabold text-slate-800">{groups.upcoming.length}</div>
          </div>
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col">
            <div className="flex items-center gap-2 text-slate-500 font-semibold text-[13px] mb-2">
              <CheckCircle2 size={16} className="text-emerald-500" /> Complete
            </div>
            <div className="text-3xl font-extrabold text-slate-800">{groups.completed.length}</div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
          </div>
        ) : loadError ? (
          <div className="text-center py-20 text-slate-500">{loadError}</div>
        ) : (
          <div className="space-y-6">
            {renderTableSection('overdue', 'Not Started / Overdue', groups.overdue)}
            {renderTableSection('today', 'In Progress / Today', groups.today)}
            {renderTableSection('upcoming', 'Upcoming', groups.upcoming)}
            {renderTableSection('completed', 'Completed', groups.completed)}
          </div>
        )}

        {editingTask && (
          <TaskModal 
            task={editingTask} 
            currentUser={currentUser}
            onClose={() => setEditingTask(null)} 
            onSaved={() => {
              setEditingTask(null);
              loadTasks();
            }}
          />
        )}
      </div>
    </div>
  );
};

const TaskModal = ({ task, currentUser, onClose, onSaved }) => {
  const { showToast } = useToast();
  const isEdit = !!task.id;
  const [saving, setSaving] = useState(false);
  const [leadsList, setLeadsList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  
  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoadingOptions(true);
        const [leadsRes, usersRes] = await Promise.all([
          leadsAPI.getAll({ limit: 500 }),
          usersAPI.getAll().catch(() => ({ data: [] }))
        ]);

        const raw = leadsRes.data;
        const leadsArray = Array.isArray(raw) ? raw : Array.isArray(raw?.leads) ? raw.leads : Array.isArray(raw?.data) ? raw.data : [];
        setLeadsList(leadsArray);

        const usersRaw = usersRes.data;
        const usersArray = Array.isArray(usersRaw) ? usersRaw : [];
        setUsersList(usersArray);
      } catch (err) {
        setLeadsList([]);
        setUsersList([]);
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  const formatForInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
  };

  const [formData, setFormData] = useState({
    title: task.title || '',
    description: task.description || '',
    due_date: formatForInput(task.due_date) || '',
    priority: task.priority || 'medium',
    status: task.status || 'pending',
    lead_id: task.lead_id || '',
    assigned_to_id: task.assigned_to_id || currentUser?.id || ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) { showToast('Title is required', 'error'); return; }

    setSaving(true);
    const payload = { ...formData };
    if (payload.due_date) payload.due_date = new Date(payload.due_date).toISOString();
    else payload.due_date = null;
    
    if (payload.lead_id) payload.lead_id = parseInt(payload.lead_id, 10);
    else payload.lead_id = null;
    
    if (payload.assigned_to_id) payload.assigned_to_id = parseInt(payload.assigned_to_id, 10);
    
    if (!isEdit) payload.created_by_id = currentUser?.id;

    try {
      if (isEdit) await tasksAPI.update(task.id, payload);
      else await tasksAPI.create(payload);
      showToast('Task saved successfully', 'success');
      onSaved();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to save task', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Safe arrays for dropdowns
  const safeLeads = Array.isArray(leadsList) ? leadsList : [];
  const safeUsers = Array.isArray(usersList) ? usersList : [];

  const leadOptions = safeLeads.map(l => ({ value: l.id, label: `${l.name} ${l.phone ? `(${l.phone})` : ''}` }));
  if (task.lead_id && !safeLeads.find(l => l.id == task.lead_id)) {
    leadOptions.push({ value: task.lead_id, label: task.lead_name || `Lead #${task.lead_id}` });
  }

  const userOptions = safeUsers.map(u => ({ value: u.id, label: `${u.full_name || u.username} (${u.role})` }));
  if (task.assigned_to_id && !safeUsers.find(u => u.id == task.assigned_to_id)) {
    userOptions.push({ value: task.assigned_to_id, label: task.assigned_to_name || `User #${task.assigned_to_id}` });
  }
  if (!userOptions.length && currentUser) {
    userOptions.push({ value: currentUser.id, label: `${currentUser.full_name || currentUser.username} (You)` });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-visible flex flex-col animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 shrink-0">
          <h2 className="text-xl font-bold text-[#1E3A5F]">
            {isEdit ? 'Edit Task' : 'Add New Task'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors p-1 rounded-md hover:bg-slate-100">
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-6 overflow-visible">
          <form id="task-form" onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Task Title <span className="text-rose-500">*</span></label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                placeholder="What needs to be done?"
                className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none"
              />
            </div>

            <div>
              <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={2}
                placeholder="Add optional notes..."
                className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4 relative z-20">
              <div>
                <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Related Lead</label>
                <SearchableSelect
                  options={leadOptions}
                  value={formData.lead_id}
                  onChange={(val) => setFormData(prev => ({ ...prev, lead_id: val }))}
                  placeholder={loadingOptions ? "Loading..." : "Select Lead..."}
                  disabled={loadingOptions}
                />
              </div>
              <div>
                <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Assign To <span className="text-rose-500">*</span></label>
                <SearchableSelect
                  options={userOptions}
                  value={formData.assigned_to_id}
                  onChange={(val) => setFormData(prev => ({ ...prev, assigned_to_id: val }))}
                  placeholder={loadingOptions ? "Loading..." : "Select Assignee..."}
                  disabled={loadingOptions}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 relative z-10">
              <div>
                <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Priority</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none appearance-none font-semibold text-slate-700"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[14px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none appearance-none font-semibold text-slate-700"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-[13px] font-bold text-slate-700 mb-1.5">Start/Due Date</label>
                <input
                  type="datetime-local"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleChange}
                  className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-2.5 text-[13px] focus:bg-white focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] transition-all outline-none font-semibold text-slate-700"
                />
              </div>
            </div>

          </form>
        </div>

        <div className="px-6 py-5 border-t border-slate-100 flex justify-end gap-3 shrink-0 bg-slate-50/50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-slate-200 text-slate-600 bg-white rounded-xl hover:bg-slate-50 text-[13px] font-bold transition-colors shadow-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="task-form"
            disabled={saving}
            className="px-6 py-2 bg-[#1E3A5F] hover:bg-[#132845] text-white rounded-xl text-[13px] font-bold disabled:opacity-50 transition-all flex items-center gap-2 shadow-sm shadow-[#1E3A5F]/30"
          >
            {saving && <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" /> }
            {isEdit ? 'Save Changes' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Tasks;